import React from 'react';
import PropTypes from 'prop-types';
import { useBeforeunload } from 'react-beforeunload';
import { get } from 'lodash';
import { exerciseTypes } from 'z-js-commons';
import styled from 'styled-components';

import Barrage from './barrage';
import DragDrop from './drag-drop';
import FillBlanks from './fill-blanks';
import FillChoice from './fill-choice';
import FindError from './find-error';
import MultipleChoice from './multiple-choice';
import NotImplemented from './not-implemented';
import OpenAnswer from './open-answer';
import TrueFalse from './true-false';
import Checker from './checker';
import SharedText from './shared-text';
import ActionsBar from './components/actions-bar';
import ExerciseHeading, { headingDataPropType } from './components/heading';
import Feedback from '../feedback';
import FluidContainer from '../layouts/components/fluid-container';
import Stack from '../stack';
import { Body } from '../typography';

const Divider = styled.hr`
    width: 100%;
`;

const selectExerciseComponent = (exerciseTypeLabel) => {
    const exerciseType = exerciseTypes.getExerciseTypeFromName(exerciseTypeLabel);

    switch (exerciseType) {
        case exerciseTypes.DRAG_DROP:
            return DragDrop;
        case exerciseTypes.FILL_BLANKS:
            return FillBlanks;
        case exerciseTypes.FILL_CHOICE:
            return FillChoice;
        case exerciseTypes.FIND_ERROR:
            return FindError;
        case exerciseTypes.MULTIPLE_CHOICE:
            return MultipleChoice;
        case exerciseTypes.OPEN_ANSWER:
            return OpenAnswer;
        case exerciseTypes.SHOTS:
            return Barrage;
        case exerciseTypes.TRUE_FALSE:
            return TrueFalse;
        case exerciseTypes.CHECKER:
            return Checker;
        case exerciseTypes.SHARED_TEXT:
            return SharedText;
        default:
            return NotImplemented;
    }
};

const Exercise = ({
    answers = [],
    correctable,
    // TODO: avoid panic when exercise is not the expected shape
    exercise = { attachments: { images: {} } },
    homeworkTerminated,
    onAnswer,
    onCorrection,
    onPlayVideo,
    profile = {},
    videos = [],
    correctionStatus = null,
    showTitle = true,
    showPrompt = true,
    showSharedText = true,
    showAttachments = true,
    showFeedbacks = true,
    showActionsBar = true,
    showHintBox = true,
    solution,
    validateCheckerStep,
    ignoreBeforeUnload = false,
    headingData = null,
    ...rest
}) => {
    const ExerciseComponent = selectExerciseComponent(exercise.typeLabel);

    const hasAttachments = {
        audio: !!get(exercise, 'attachments.audio'),
        images: !!get(exercise, 'attachments.images') && get(exercise, 'attachments.images').length > 0,
        sharedText: !!get(exercise, 'sharedText'),
        video: !!get(exercise, 'attachments.video'),
        geogebra: !!get(exercise, 'attachments.geogebra'),
    };

    const show = {
        title: showTitle,
        prompt: showPrompt,
        sharedText: showSharedText,
        attachments: showAttachments,
        hintBox: showHintBox,
    };

    useBeforeunload((event) => {
        if (!homeworkTerminated && !ignoreBeforeUnload) {
            event.preventDefault();
        }
    });

    if (!exercise.id) {
        return null;
    }

    return (
        <FluidContainer className="ck-content" flexDirection="column">
            {!!headingData && <ExerciseHeading headingData={headingData} />}
            <ExerciseComponent
                answers={answers}
                correctable={correctable}
                exercise={exercise}
                onAnswer={onAnswer}
                profile={profile}
                correctionStatus={correctionStatus}
                onPlayVideo={onPlayVideo}
                videos={videos}
                show={show}
                solution={solution}
                hasAttachments={hasAttachments}
                validateCheckerStep={validateCheckerStep}
                {...rest}
            />
            {!!exercise.origin && !!exercise.originOther && (
                <>
                    <Divider />
                    <Body lv={5}>{`Fonte: ${exercise.originOther}`}</Body>
                </>
            )}
            <Stack gap="desktop">
                {showFeedbacks && !!correctionStatus && (
                    <Feedback answers={answers} exercise={exercise} correctionStatus={correctionStatus} />
                )}
                {showActionsBar && (
                    <ActionsBar
                        answers={answers}
                        exercise={exercise}
                        homeworkTerminated={homeworkTerminated}
                        onCorrection={onCorrection}
                        profile={profile}
                    />
                )}
            </Stack>
        </FluidContainer>
    );
};

Exercise.propTypes = {
    answers: PropTypes.any,
    correctable: PropTypes.bool,
    exercise: PropTypes.any,
    homeworkTerminated: PropTypes.bool,
    onAnswer: PropTypes.any,
    onCorrection: PropTypes.func,
    onPlayVideo: PropTypes.func,
    profile: PropTypes.object,
    correctionStatus: PropTypes.object,
    videos: PropTypes.array,
    /* Those shows should be removed when non-exercises specific thinghy are moved */
    showTitle: PropTypes.bool,
    showPrompt: PropTypes.bool,
    showSharedText: PropTypes.bool,
    showAttachments: PropTypes.bool,
    showFeedbacks: PropTypes.bool,
    showActionsBar: PropTypes.bool,
    showHintBox: PropTypes.bool,
    solution: PropTypes.bool,
    validateCheckerStep: PropTypes.func,
    ignoreBeforeUnload: PropTypes.bool,
    headingData: headingDataPropType,
};

export default Exercise;
